import { register } from '!/repository'
import { api } from '@setplex/tria-api'
import { combine, createEvent, createStore, sample } from 'effector'
import { type EpgDateEx } from '~/entities/epg/dates'
import { profilesModel } from '~/entities/profiles'
import { model as session } from '~/entities/session'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import * as pinLockFeature from '~/features/pin-lock'
import { model as clock } from '~/shared/clock'
import { aye, F, id, noop, T } from '~/shared/helpers'
import { epgCategoriesWidgetModel } from '~/widgets/epg/categories'
import { epgChannelsWidgetModel } from '~/widgets/epg/channels'
import { epgDatesWidgetModel } from '~/widgets/epg/dates'
import { epgInfoWidgetModel } from '~/widgets/epg/info'
import { epgPlayerWidgetModel } from '~/widgets/epg/player'
import { epgProgramsWidgetModel } from '~/widgets/epg/programs'
import { epgSidebarWidgetModel } from '~/widgets/epg/sidebar'
import { epgTimelineWidgetModel } from '~/widgets/epg/timeline'
import * as epg from './epg'
import * as skeleton from './skeleton'
import { widgetsPaymentExternal } from '~/widgets/Payment/ExternalPaymentOpen'

export const init = createEvent<{ query: URLSearchParams }>()
export const left = createEvent()
export const reset = createEvent()

export const $isOnThePage = createStore(false).on(init, T).on(left, F)

// init all widgets
sample({
  clock: init,
  target: [
    skeleton.init, // init skeleton on page init
    epgCategoriesWidgetModel.init,
    epgChannelsWidgetModel.init,
    epgDatesWidgetModel.init,
    epgInfoWidgetModel.init,
    epgPlayerWidgetModel.init,
    epgProgramsWidgetModel.init,
    epgSidebarWidgetModel.init,
    epgTimelineWidgetModel.init,
    clock.setup, // setup application clock just in case
  ],
})

export const reloadEpgTvPlayerUrl = createEvent()

sample({
  clock: [
    ssePurchasesModel.$channelIds,
    ssePurchasesModel.$channelSetIds,
    reloadEpgTvPlayerUrl,
  ],
  source: { playing: epgPlayerWidgetModel.$playing, isOnThePage: $isOnThePage },
  filter: ({ playing, isOnThePage }) => aye(playing) && isOnThePage,
  fn: ({ playing }) => ({ channelId: playing!.id }),
  target: epgPlayerWidgetModel.fetchUrl,
})

// reset all widgets on page left
// this could involve partial cleanup for temporary data
sample({
  clock: left,
  target: [
    skeleton.left, // reset skeleton on page left
    epgCategoriesWidgetModel.left,
    epgChannelsWidgetModel.left,
    epgDatesWidgetModel.left,
    epgInfoWidgetModel.left,
    epgPlayerWidgetModel.left,
    epgProgramsWidgetModel.left,
    epgSidebarWidgetModel.left,
    epgTimelineWidgetModel.left,
  ],
})

sample({
  clock: left,
  target: api.player.resetCurrentPlayerPageUrlError,
})

// reset all widgets on any session change
// this could involve full cleanup
sample({
  clock: reset,
  target: [
    skeleton.reset, // reset skeleton on page reset
    epgCategoriesWidgetModel.reset,
    epgChannelsWidgetModel.reset,
    epgDatesWidgetModel.reset,
    epgInfoWidgetModel.reset,
    epgPlayerWidgetModel.reset,
    epgProgramsWidgetModel.reset,
    epgSidebarWidgetModel.reset,
    epgTimelineWidgetModel.reset,
  ],
})

//
// page logic connections
//

// reset all widgets on any session change
sample({
  clock: [session.$accountNumber, profilesModel.$activeProfileId],
  fn: noop,
  target: reset,
})

//
// widgets connections
//

sample({
  clock: epgChannelsWidgetModel.openPinlockPopup,
  fn: ({ id, formattedName, name, locked }) => ({
    ...pinLockFeature.emptyPinLockState,
    id,
    name: formattedName ?? name,
    locked,
  }),
  target: pinLockFeature.openPinLockPopup,
})

export const $epgActiveChannel = combine(
  selectEpgChannelModel.$active,
  ssePurchasesModel.$channel,
  (channel, adjust) => (channel ? adjust(channel) : null)
)

// set prev probing channel on payment external close popup
sample({
  clock: widgetsPaymentExternal.closePopup,
  source: $epgActiveChannel,
  filter: (channel) => channel !== null,
  fn: (channel) => channel!.id,
  target: epgChannelsWidgetModel.$probingId,
})

//
// skin
//

// categories
register(epgCategoriesWidgetModel.$categories, '$epg/categories')
register(epgCategoriesWidgetModel.$isLast, '$epg/categories/isLast')
register(epgCategoriesWidgetModel.$selected, '$epg/categories/selected')
register(epgCategoriesWidgetModel.$isCategorySelectedPipe, '|epg/categories/isSelected') // biome-ignore format: one line
register(epgCategoriesWidgetModel.more, 'epg/categories/more')
register(epgCategoriesWidgetModel.select.prepend<{ id: number }>(id), 'epg/categories/select') // biome-ignore format: one line

// dates
register(epgDatesWidgetModel.$list, '$epg/dates/list')
register(epgDatesWidgetModel.$isDateSelectedPipe, '|epg/dates/isSelected')
register(epgDatesWidgetModel.select.prepend<{ date: EpgDateEx }>(({ date }) => date), 'epg/dates/select') // biome-ignore format: one line

// programs timeline
register(epgTimelineWidgetModel.$timeline, '$epg/timeline')
register(clock.$minutes, '$epg/timeline/now')
register(epgTimelineWidgetModel.$start, '$epg/timeline/start')
register(epgTimelineWidgetModel.$formatTimePipe, '|epg/timeline/formatTime')
register(epgTimelineWidgetModel.updateStart, 'epg/timeline/updateStart')
register(epgTimelineWidgetModel.updateTimeline, 'epg/timeline/update')

// programs epg
register(epg.$live, '$epg/programs')
register(epgProgramsWidgetModel.probe, 'epg/programs/probe')
register(epgProgramsWidgetModel.select, 'epg/programs/select')

// channels
register(epgChannelsWidgetModel.$selected, '$epg/channels/selected')
register(epgChannelsWidgetModel.$isChannelSelectedPipe, '|epg/channels/isSelected') // biome-ignore format: one line
register(epgChannelsWidgetModel.more, 'epg/channels/more')
register(epgChannelsWidgetModel.probe.prepend<{ id: number }>(id), 'epg/channels/probe') // biome-ignore format: one line
register(epgChannelsWidgetModel.likeOrUnlike, 'epg/channels/likeOrUnlike')

// sidebar
register(epgSidebarWidgetModel.$info, '$epg/sidebar')
register(epgSidebarWidgetModel.$upcoming, '$epg/sidebar/upcoming')
register(epgSidebarWidgetModel.$open, '$epg/sidebar/isOpen')
register(epgSidebarWidgetModel.close, 'epg/sidebar/close')

// info
register(epgInfoWidgetModel.$playing, '$epg/info')

// skeleton
register(skeleton.$ready, '$epg/ready')
register(skeleton.$contentLoading, '$epg/contentLoading')
